import React, { useEffect, useState } from "react";
import { useCart } from "../context/CartContext";
import { useAuth } from "../context/AuthContext";
import { api, formatApiError } from "../lib/api";
import { useNavigate, Link } from "react-router-dom";
import { ArrowRight, ChevronLeft, CheckCircle2, Loader2, Shield, Users, ShoppingBag, CreditCard } from "lucide-react";
import Footer from "../components/Footer";
import Honeypot from "../components/Honeypot";

export default function Checkout() {
  const { items, subtotal, clear } = useCart();
  const { user } = useAuth();
  const navigate = useNavigate();

  const [form, setForm] = useState({
    contact_name: user?.name || "",
    contact_email: user?.email || "",
    contact_phone: "",
    travel_window: "",
    travellers: 1,
    notes: "",
  });
  const [accepted, setAccepted] = useState(false);
  const [website, setWebsite] = useState(""); // honeypot
  const [loading, setLoading] = useState(false);
  const [err, setErr] = useState("");
  const [confirm, setConfirm] = useState(null);
  const [payLoading, setPayLoading] = useState(false);

  useEffect(() => {
    if (items.length === 0 && !confirm) navigate("/cart", { replace: true });
  }, [items.length, confirm, navigate]);

  const setF = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const payNow = async () => {
    if (!confirm?.id) return;
    setPayLoading(true);
    setErr("");
    try {
      const { data } = await api.post(`/orders/${confirm.id}/checkout`, {
        order_id: confirm.id,
        origin_url: window.location.origin,
      });
      if (data?.url) {
        window.location.href = data.url;
      } else {
        setErr("Could not initiate secure payment. Please try again.");
        setPayLoading(false);
      }
    } catch (e) {
      setErr(formatApiError(e.response?.data?.detail) || e.message);
      setPayLoading(false);
    }
  };

  const submit = async (e) => {
    e.preventDefault();
    if (website) return; // honeypot tripped
    setErr("");
    if (!accepted) {
      setErr("Please accept the terms & conditions to continue.");
      return;
    }
    setLoading(true);
    try {
      const payload = {
        ...form,
        travellers: Number(form.travellers) || 1,
        accepted_terms: accepted,
        website,
        items: items.map((it) => ({
          id: it.id,
          kind: it.kind,
          name: it.name,
          sub: it.sub || null,
          price_eur: it.price_eur,
          qty: it.qty,
        })),
        subtotal_eur: subtotal,
      };
      const { data } = await api.post("/orders", payload);
      setConfirm(data);
      clear();
    } catch (e) {
      setErr(formatApiError(e.response?.data?.detail) || e.message);
    } finally {
      setLoading(false);
    }
  };

  if (confirm) {
    return (
      <div data-testid="checkout-confirm" className="pt-32 pb-16 min-h-screen">
        <div className="max-w-2xl mx-auto px-6">
          <div className="iq-card p-12 text-center animate-fade-up">
            <CheckCircle2 className="h-14 w-14 text-gold mx-auto mb-5" />
            <h1 className="font-display text-cream text-4xl tracking-wider mb-2">Reservation received</h1>
            <p className="text-cream-dim text-sm font-display tracking-[0.3em] mb-6">REF #{confirm.reference}</p>
            <p className="font-serif italic text-cream text-lg leading-relaxed mb-6">
              "{confirm.message || 'Our conductor will confirm your final quote within one business day, with operator availability and a secure payment link.'}"
            </p>

            {/* Pay-in-full CTA */}
            {confirm.total_eur > 0 && (
              <div data-testid="ck-pay-block" className="border border-gold/40 bg-gold/5 rounded-sm p-6 mb-6 text-left">
                <div className="iq-overline text-gold mb-2">Lock your inventory · pay in full</div>
                <div className="flex items-baseline justify-between mb-3">
                  <span className="font-display text-cream tracking-wider">Total due now</span>
                  <span className="font-display text-gold text-3xl">€{Number(confirm.total_eur).toLocaleString()}</span>
                </div>
                <p className="text-xs text-cream-dim/80 leading-relaxed mb-4">
                  Card, Apple Pay, Google Pay and Klarna all accepted at Stripe Checkout. Your card details never touch our servers. Full refund if our conductor cannot confirm availability.
                </p>
                <button
                  data-testid="ck-pay-now"
                  onClick={payNow}
                  disabled={payLoading}
                  className="iq-btn-primary w-full justify-center disabled:opacity-50"
                >
                  {payLoading ? (
                    <><Loader2 className="h-5 w-5 animate-spin" /> Redirecting to secure payment…</>
                  ) : (
                    <><CreditCard className="h-4 w-4" /> Pay €{Number(confirm.total_eur).toLocaleString()} securely</>
                  )}
                </button>
                <div className="mt-3 flex items-center gap-2 text-[10px] text-cream-dim/70 tracking-widest uppercase">
                  <span className="px-2 py-0.5 border border-panel-border rounded-sm">Card</span>
                  <span className="px-2 py-0.5 border border-panel-border rounded-sm"></span>
                  <span className="px-2 py-0.5 border border-panel-border rounded-sm">G Pay</span>
                  <span className="px-2 py-0.5 border border-panel-border rounded-sm">Klarna</span>
                </div>
              </div>
            )}

            <div className="text-sm text-cream-dim space-y-1 mb-6">
              <p>Confirmation will be sent to <span className="text-cream">{form.contact_email}</span>.</p>
              <p>You can track this reservation in <Link to="/dashboard" className="text-gold hover:text-cream underline">your dashboard</Link>.</p>
            </div>

            {err && (
              <div data-testid="ck-pay-error" className="border border-accent-red/40 bg-accent-red/10 text-cream px-4 py-3 rounded-sm text-sm mb-4">{err}</div>
            )}

            <div className="flex items-center justify-center gap-3 flex-wrap">
              <Link to="/" className="iq-btn-secondary">Return home</Link>
              <Link to="/routes" className="iq-btn-secondary">Browse more tours</Link>
            </div>
          </div>
        </div>
        <Footer />
      </div>
    );
  }

  return (
    <div data-testid="checkout-page" className="pt-safe-navbar-lg pb-16">
      <div className="max-w-[1100px] mx-auto px-6">
        <Link to="/cart" className="inline-flex items-center gap-1 text-cream-dim hover:text-gold text-sm mb-5">
          <ChevronLeft className="h-4 w-4" /> Back to cart
        </Link>
        <div className="mb-8">
          <div className="iq-overline mb-2"><ShoppingBag className="inline h-3 w-3 mr-1" /> Checkout</div>
          <h1 className="font-display text-cream text-5xl md:text-6xl tracking-wider">Confirm your reservation</h1>
          <p className="font-serif italic text-cream-dim text-lg mt-2">No card needed yet — our conductor verifies availability first.</p>
        </div>

        <div className="grid lg:grid-cols-[1fr_340px] gap-6">
          <form data-testid="checkout-form" onSubmit={submit} className="iq-card p-6 md:p-8 space-y-5">
            <Honeypot value={website} onChange={setWebsite} name="website" />
            <div className="grid md:grid-cols-2 gap-5">
              <div>
                <label className="iq-label">Contact name *</label>
                <input data-testid="ck-name" required className="iq-input" value={form.contact_name} onChange={(e) => setF("contact_name", e.target.value)} />
              </div>
              <div>
                <label className="iq-label">Email *</label>
                <input data-testid="ck-email" required type="email" className="iq-input" value={form.contact_email} onChange={(e) => setF("contact_email", e.target.value)} />
              </div>
              <div>
                <label className="iq-label">Phone</label>
                <input data-testid="ck-phone" className="iq-input" value={form.contact_phone} onChange={(e) => setF("contact_phone", e.target.value)} placeholder="+44 …" />
              </div>
              <div>
                <label className="iq-label">Travellers *</label>
                <input data-testid="ck-travellers" required type="number" min="1" max="500" className="iq-input" value={form.travellers} onChange={(e) => setF("travellers", e.target.value)} />
              </div>
            </div>
            <div>
              <label className="iq-label">Travel window *</label>
              <input data-testid="ck-window" required className="iq-input" value={form.travel_window} onChange={(e) => setF("travel_window", e.target.value)} placeholder="July 2026, 10 days" />
            </div>
            <div>
              <label className="iq-label">Notes (special requirements, accessibility, supervised minors…)</label>
              <textarea data-testid="ck-notes" className="iq-input min-h-[80px]" value={form.notes} onChange={(e) => setF("notes", e.target.value)} placeholder="Anything we should know?" />
            </div>

            {/* T&C */}
            <div className="border border-panel-border rounded-sm bg-ink p-4">
              <label className="flex items-start gap-3 cursor-pointer">
                <input
                  data-testid="ck-terms"
                  type="checkbox"
                  checked={accepted}
                  onChange={(e) => setAccepted(e.target.checked)}
                  className="mt-1 h-4 w-4 accent-gold cursor-pointer"
                />
                <span className="text-sm text-cream-dim leading-relaxed">
                  I accept the InterQuest <Link to="/terms" className="text-gold hover:text-cream underline">Terms &amp; Conditions</Link> and acknowledge that the quoted total is indicative until our conductor confirms availability and issues a binding final invoice. No charge is made until I authorize payment via the secure link in my email.
                </span>
              </label>
            </div>

            {err && (
              <div data-testid="ck-error" className="border border-accent-red/40 bg-accent-red/10 text-cream px-4 py-3 rounded-sm text-sm">{err}</div>
            )}

            <div className="flex items-center justify-between gap-3 flex-wrap">
              <p className="text-[11px] text-cream-dim/60 italic">Conductor responds within one business day.</p>
              <button data-testid="ck-submit" type="submit" disabled={loading || !accepted} className="iq-btn-primary disabled:opacity-50">
                {loading ? <Loader2 className="h-5 w-5 animate-spin" /> : <>Reserve <ArrowRight className="h-4 w-4" /></>}
              </button>
            </div>
          </form>

          {/* Order summary */}
          <aside className="space-y-4">
            <div className="iq-card p-5 sticky top-24">
              <div className="iq-overline mb-3">Order summary</div>
              <div className="space-y-2 max-h-72 overflow-y-auto mb-3">
                {items.map((it) => (
                  <div key={it.id} className="flex items-center justify-between gap-2 text-sm">
                    <div className="min-w-0">
                      <div className="text-cream truncate">{it.name}</div>
                      <div className="text-cream-dim/70 text-[11px]">× {it.qty}</div>
                    </div>
                    <span className="text-gold font-display shrink-0">€{(it.price_eur * it.qty).toLocaleString()}</span>
                  </div>
                ))}
              </div>
              <div className="border-t border-panel-border pt-3 flex items-center justify-between">
                <span className="font-display text-cream tracking-wider">Total</span>
                <span className="font-display text-gold text-2xl">€{subtotal.toLocaleString()}</span>
              </div>
              <div className="mt-2 flex items-baseline justify-between text-sm">
                <span className="text-cream-dim">Due at checkout</span>
                <span data-testid="ck-total-preview" className="font-display text-gold">€{Math.round(subtotal).toLocaleString()}</span>
              </div>
              <p className="text-[10px] text-cream-dim/60 italic mt-1">Full refund if conductor cannot confirm availability.</p>
            </div>
            <div className="iq-card p-4 text-xs text-cream-dim/80 space-y-2">
              <div className="flex items-start gap-2"><Shield className="h-4 w-4 text-gold mt-0.5 shrink-0" /> <span>Properties are pre-vetted via Safety Score v2.</span></div>
              <div className="flex items-start gap-2"><Users className="h-4 w-4 text-gold mt-0.5 shrink-0" /> <span>Group rates auto-apply for 10+ travellers.</span></div>
            </div>
          </aside>
        </div>
      </div>
      <Footer />
    </div>
  );
}
